import type React from "react";
import { useTabs, type TabType } from "@/contexts/tabs-context";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
	History,
	MessagesSquare,
	FileText,
	Presentation,
	Video,
	Archive,
	KeyRound,
	Bot,
	FolderKanban,
	Clock,
	RotateCcw,
	Trash2,
} from "lucide-react";
import { useMemo } from "react";
import { useWorkspaceContext } from "@/contexts/workspace-context";

const getTabIcon = (type: TabType) => {
	const icons = {
		chat: MessagesSquare,
		doc: FileText,
		board: Presentation,
		meet: Video,
		storage: Archive,
		password: KeyRound,
		automation: Bot,
		project: FolderKanban,
	};
	return icons[type] || MessagesSquare;
};

const getTabTypeColor = (type: TabType) => {
	const colors = {
		chat: "text-chart-1",
		doc: "text-chart-2",
		board: "text-chart-4",
		meet: "text-chart-2",
		storage: "text-chart-1",
		password: "text-chart-3",
		automation: "text-chart-5",
		project: "text-chart-4",
	};
	return colors[type] || "text-sidebar-foreground";
};

const formatTimeAgo = (date: Date): string => {
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMs / 3600000);
	const diffDays = Math.floor(diffMs / 86400000);

	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins} min ago`;
	if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
	if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

	return date.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
	});
};

export const TabHistoryDropdown: React.FC = () => {
	const { recentlyVisited, recentlyClosed, setActiveTab, openTab, restoreTab, clearHistory } =
		useTabs();
	const { channels } = useWorkspaceContext();
	const channelTitleById = useMemo(() => {
		const map = new Map<string, string>();
		for (const channel of channels) {
			map.set(String(channel.id), channel.title ?? `Channel ${channel.id}`);
		}
		return map;
	}, [channels]);
	const getTabDisplayName = (tab: (typeof recentlyVisited)[0]["tab"]) =>
		channelTitleById.get(tab.channelId) ?? tab.name;

	const handleOpenRecent = (entry: (typeof recentlyVisited)[0]) => {
		const displayName = getTabDisplayName(entry.tab);
		openTab(
			{
				channelId: entry.tab.channelId,
				workspaceId: entry.tab.workspaceId,
				name: displayName,
				type: entry.tab.type,
				isPinned: false,
				path: entry.tab.path,
			},
			undefined,
			undefined,
			undefined,
			entry.tab.id
		);
	};

	const handleRestoreClosed = (entry: (typeof recentlyClosed)[0]) => {
		restoreTab(entry.tab);
	};

	const hasHistory = recentlyVisited.length > 0 || recentlyClosed.length > 0;

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="h-7 px-2 text-sidebar-foreground hover:bg-sidebar-accent/50"
					title="Tab History"
				>
					<History className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="center"
				className="max-h-[500px] w-80 overflow-y-auto border-sidebar-border bg-sidebar"
			>
				{!hasHistory ? (
					<div className="p-4 text-center text-sidebar-foreground/70 text-sm">
						No tab history yet
					</div>
				) : (
					<>
						{/* Recently Visited */}
						{recentlyVisited.length > 0 && (
							<>
								<DropdownMenuLabel className="flex items-center gap-2 text-sidebar-foreground">
									<Clock className="h-4 w-4" />
									Recently Visited
								</DropdownMenuLabel>
								{recentlyVisited.map((entry, index) => {
									const Icon = getTabIcon(entry.tab.type);
									const displayName = getTabDisplayName(entry.tab);
									return (
										<DropdownMenuItem
											key={`visited-${entry.tab.id}-${index}`}
											onClick={() => handleOpenRecent(entry)}
											className="flex cursor-pointer items-center justify-between gap-2 text-sidebar-foreground hover:bg-sidebar-accent"
										>
											<div className="flex min-w-0 flex-1 items-center gap-2">
												<Icon
													className={`h-4 w-4 flex-shrink-0 ${getTabTypeColor(entry.tab.type)}`}
												/>
												<span className="truncate text-sm">
													{displayName}
												</span>
											</div>
											<span className="flex-shrink-0 text-sidebar-foreground/70 text-xs">
												{entry.visitedAt && formatTimeAgo(entry.visitedAt)}
											</span>
										</DropdownMenuItem>
									);
								})}
							</>
						)}

						{/* Separator if both sections have content */}
						{recentlyVisited.length > 0 && recentlyClosed.length > 0 && (
							<DropdownMenuSeparator />
						)}

						{/* Recently Closed */}
						{recentlyClosed.length > 0 && (
							<>
								<DropdownMenuLabel className="flex items-center gap-2 text-sidebar-foreground">
									<RotateCcw className="h-4 w-4" />
									Recently Closed
								</DropdownMenuLabel>
								{recentlyClosed.map((entry, index) => {
									const Icon = getTabIcon(entry.tab.type);
									const displayName = getTabDisplayName(entry.tab);
									return (
										<DropdownMenuItem
											key={`closed-${entry.tab.id}-${index}`}
											onClick={() => handleRestoreClosed(entry)}
											className="group flex cursor-pointer items-center justify-between gap-2 text-sidebar-foreground hover:bg-sidebar-accent"
										>
											<div className="flex min-w-0 flex-1 items-center gap-2">
												<Icon
													className={`h-4 w-4 flex-shrink-0 ${getTabTypeColor(entry.tab.type)}`}
												/>
												<span className="truncate text-sm">
													{displayName}
												</span>
											</div>
											<div className="flex items-center gap-2">
												<span className="flex-shrink-0 text-sidebar-foreground/70 text-xs">
													{entry.closedAt && formatTimeAgo(entry.closedAt)}
												</span>
												{/* <span className="text-sidebar-primary text-xs opacity-0 transition-opacity group-hover:opacity-100">
													Restore
												</span> */}
											</div>
										</DropdownMenuItem>
									);
								})}
							</>
						)}

						{/* Clear History */}
						<DropdownMenuSeparator />
						<DropdownMenuItem
							onClick={clearHistory}
							className="flex cursor-pointer items-center gap-2 text-destructive hover:bg-sidebar-accent"
						>
							<Trash2 className="h-4 w-4" />
							<span className="text-sm">Clear History</span>
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
